import { BudgetBakersInstallmentsValidationResultEnum } from '.spec/self-care/tsTypes';
import React, { useMemo } from 'react';
import { BUDGET_BAKERS_COMPLETE_MODAL } from 'sections/KYC/constants';
import { KYCModalType } from 'sections/KYC/types/modals';

import { BudgetBakersResponse } from '@skippay/core/@types/budgetBakers';
import type { InstallmentApplication } from '@skippay/core/@types/installments';
import { ReactComponent as IconSad } from '@skippay/core/assets/illustrations/icon-brambora-sad.svg';
import { ReactComponent as IconSmile } from '@skippay/core/assets/illustrations/icon-brambora-smile.svg';
import Button from '@skippay/core/components/Button';
import useGoogleAnalytics from '@skippay/core/components/GoogleAnalytics/useGoogleAnalytics';
import Modal from '@skippay/core/components/Modal';
import ModalContent from '@skippay/core/components/ModalContent';
import Text from '@skippay/core/components/Text';
import { FormattedMessage, useIntl } from '@skippay/core/i18n/intl';

interface Props {
	type: KYCModalType;
	onBudgetBakersVerification: (
		option: Omit<Pick<InstallmentApplication, 'budgetBakers'>['budgetBakers'], 'authorization'>
	) => void;
	validationResult?: BudgetBakersResponse['validationResult'];
	handleTrackScreen?: (screen: typeof BUDGET_BAKERS_COMPLETE_MODAL) => void;
}

function BudgetBakersCompleteModal(props: Props): JSX.Element {
	const intl = useIntl();

	const { onBudgetBakersVerification, type, validationResult } = props;

	const { sendEvent } = useGoogleAnalytics();

	const isApproved = validationResult === BudgetBakersInstallmentsValidationResultEnum.APPROVED;

	const isDenied = validationResult === BudgetBakersInstallmentsValidationResultEnum.DENIED;

	const content = useMemo(() => {
		if (isApproved) {
			return {
				title: (
					<FormattedMessage
						id="BudgetBakersCompleteModal.heading.approved"
						defaultMessage="To šlo jako po másle"
					/>
				),
				text: (
					<FormattedMessage
						id="BudgetBakersCompleteModal.text.approved"
						defaultMessage="Vaše příjmy jsme úspěšně ověřili a vy tak můžete {br}pokračovat ve vyplňování žádosti."
						values={{
							br: <br className="hidden sm:block" />,
						}}
					/>
				),
				buttonText: (
					<FormattedMessage
						id="BudgetBakersCompleteModal.button.continue"
						defaultMessage="Pokračovat"
					/>
				),
				icon: <IconSmile />,
			};
		}

		if (isDenied) {
			return {
				title: (
					<FormattedMessage
						id="BudgetBakersCompleteModal.heading.denied"
						defaultMessage="Bohužel to neklaplo"
					/>
				),
				text: (
					<FormattedMessage
						id="BudgetBakersCompleteModal.text.denied"
						defaultMessage="Vaši žádost jsme posoudili. Mrzí nás to, ale teď {br}ji nemůžeme schválit."
						values={{
							br: <br className="hidden sm:block" />,
						}}
					/>
				),
				buttonText: (
					<FormattedMessage
						id="BudgetBakersCompleteModal.button.backToOverview"
						defaultMessage="Zpět na hlavní přehled"
					/>
				),
				icon: <IconSad className="circleIconError h-24 w-24" />,
			};
		}

		return {
			title: <FormattedMessage id="BudgetBakersCompleteModal.heading" defaultMessage="Hotovo" />,
			text: (
				<FormattedMessage
					id="BudgetBakersCompleteModal.text"
					defaultMessage="Získali jsme potřebná data pro ověření vašeho příjmu. {br}Hned se na ně podíváme."
					values={{
						br: <br className="hidden sm:block" />,
					}}
				/>
			),
			buttonText: (
				<FormattedMessage
					id="BudgetBakersCompleteModal.button.continue"
					defaultMessage="Pokračovat"
				/>
			),
			icon: <IconSmile />,
		};
	}, [isApproved, isDenied]);

	const handleButtonClick = () => {
		if (isApproved) {
			sendEvent({
				eCat: type,
				eAct: 'BB - Continue',
				eLab: 'Approved',
			});
		} else if (isDenied) {
			sendEvent({
				eCat: type,
				eAct: 'BB - Back to Overview',
				eLab: 'Denied',
			});
		} else {
			sendEvent({
				eCat: type,
				eAct: 'BB - Continue',
				eLab: 'Manual',
			});
		}
		onBudgetBakersVerification({ budgetBakers: { state: 'COMPLETED' } });
	};

	props.handleTrackScreen?.(BUDGET_BAKERS_COMPLETE_MODAL);

	return (
		<Modal
			contentLabel={intl.formatMessage({
				defaultMessage: 'Získali jsme potřebná data z vaší banky',
				id: 'AccountUpgrade.BudgetBakersCompleteModal.contentLabel',
			})}
			name="budgetBakersCompleteModal"
			shouldCloseOnOverlayClick={false}
			small
			isOpen
			data-clarity-unmask="true"
		>
			<ModalContent
				testId="BudgetBakersCompleteModal"
				Header={
					<Text as="h1" size="h3" align="center">
						{content.title}
					</Text>
				}
			>
				<div className="flex flex-col items-center gap-16 lg:gap-12">
					<div className="flex flex-col items-center gap-6 lg:max-w-[25rem] lg:gap-8">
						<div>{content.icon}</div>
						<Text align="center">{content.text}</Text>
					</div>
					<Button onClick={handleButtonClick} testId="continue" analyticsId="continue">
						{content.buttonText}
					</Button>
				</div>
			</ModalContent>
		</Modal>
	);
}

export default BudgetBakersCompleteModal;
